import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  useColorScheme,
  Dimensions,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { View, Text } from '../components/Themed';
import { useQuery, gql } from '@apollo/client';
import { DatePickerModal } from 'react-native-paper-dates';
import { MaterialIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Svg, { Rect, Line, Text as SvgText, G, Path } from 'react-native-svg';
import Config from '../constants/Config';

const SALES_CHARTS_QUERY = gql`
  query getSalesCharts($token: String!, $dateFrom: String!, $dateTo: String!) {
    salesCharts(token: $token, dateFrom: $dateFrom, dateTo: $dateTo) {
      totalSales
      productSales {
        id
        name
        quantity
        value
        category
      }
      categorySales {
        id
        name
        quantity
        value
      }
      computerSales {
        total
        byType {
          type
          value
          sessions
        }
        byComputer {
          id
          name
          type
          value
          sessions
          minutes
        }
      }
      dailySales {
        date
        total
        tables
        deliveries
        computers
      }
      yearOverYear {
        year
        data {
          dayOfYear
          date
          daily
          cumulative
        }
      }
      monthOverMonth {
        label
        month
        year
        data {
          dayOfMonth
          date
          daily
          cumulative
        }
      }
    }
  }
`;

const CHART_COLORS = [
  '#4CAF50', '#2196F3', '#FF9800', '#E91E63', '#9C27B0',
  '#00BCD4', '#FF5722', '#795548', '#607D8B', '#3F51B5',
];

const CHART_FONT = Platform.select({
  ios: 'Avenir-Medium',
  android: 'Roboto',
  default: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
});

const CHART_FONT_BOLD = Platform.select({
  ios: 'Avenir-Heavy',
  android: 'Roboto',
  default: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
});

function formatCurrency(value) {
  return value.toFixed(2) + ' \u20ac';
}

function formatDate(date) {
  if (!date) return '';
  const d = new Date(date);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

// Simple Bar Chart Component with Tooltip
function BarChart({ data, title, colorScheme, valueKey = 'value', labelKey = 'name', maxBars = 10 }) {
  const [selectedBar, setSelectedBar] = useState(null);
  const screenWidth = Dimensions.get('window').width - 32;
  const chartHeight = 200;
  const barWidth = Math.max(20, (screenWidth - 60) / Math.min(data.length, maxBars) - 4);

  const displayData = data.slice(0, maxBars);
  const maxValue = Math.max(...displayData.map(d => d[valueKey]), 1);

  const textColor = colorScheme === 'dark' ? '#fff' : '#333';
  const gridColor = colorScheme === 'dark' ? '#444' : '#ddd';
  const tooltipBg = colorScheme === 'dark' ? '#333' : '#fff';
  const tooltipBorder = colorScheme === 'dark' ? '#555' : '#ccc';

  const svgWidth = Math.max(screenWidth, displayData.length * (barWidth + 4) + 60);

  // Calculate bar positions for overlay
  const getBarPosition = (index) => {
    const x = index * (barWidth + 4) + 55;
    const barHeight = (displayData[index][valueKey] / maxValue) * chartHeight;
    const y = chartHeight - barHeight + 10;
    return { x, y, barHeight };
  };

  return (
    <View style={[styles.chartContainer, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#fff' }]}>
      <Text style={[styles.chartTitle, { color: textColor }]}>{title}</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View style={{ width: svgWidth, height: chartHeight + 60 }}>
          <Svg width={svgWidth} height={chartHeight + 60}>
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
              <G key={i}>
                <Line
                  x1={50}
                  y1={chartHeight - ratio * chartHeight + 10}
                  x2={displayData.length * (barWidth + 4) + 50}
                  y2={chartHeight - ratio * chartHeight + 10}
                  stroke={gridColor}
                  strokeWidth={1}
                  strokeDasharray="4,4"
                />
                <SvgText
                  x={5}
                  y={chartHeight - ratio * chartHeight + 14}
                  fill={textColor}
                  fontSize={10}
                  fontFamily={CHART_FONT}
                >
                  {formatCurrency(maxValue * ratio)}
                </SvgText>
              </G>
            ))}

            {/* Bars */}
            {displayData.map((item, index) => {
              const { x, y, barHeight } = getBarPosition(index);
              const isSelected = selectedBar === index;

              return (
                <G key={index}>
                  <Rect
                    x={x}
                    y={y}
                    width={barWidth}
                    height={barHeight}
                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                    rx={4}
                    opacity={selectedBar !== null && !isSelected ? 0.5 : 1}
                  />
                  <SvgText
                    x={x + barWidth / 2}
                    y={chartHeight + 25}
                    fill={textColor}
                    fontSize={9}
                    fontFamily={CHART_FONT}
                    textAnchor="middle"
                    transform={`rotate(-45, ${x + barWidth / 2}, ${chartHeight + 25})`}
                  >
                    {item[labelKey].substring(0, 12)}
                  </SvgText>
                </G>
              );
            })}

            {/* Tooltip */}
            {selectedBar !== null && displayData[selectedBar] && (() => {
              const item = displayData[selectedBar];
              const { x, y, barHeight } = getBarPosition(selectedBar);
              const tooltipWidth = 120;
              const tooltipHeight = 50;
              let tooltipX = x + barWidth / 2 - tooltipWidth / 2;
              let tooltipY = y - tooltipHeight - 8;
              if (tooltipX < 5) tooltipX = 5;
              if (tooltipY < 5) tooltipY = y + barHeight + 8;

              return (
                <G>
                  <Rect
                    x={tooltipX}
                    y={tooltipY}
                    width={tooltipWidth}
                    height={tooltipHeight}
                    fill={tooltipBg}
                    stroke={tooltipBorder}
                    strokeWidth={1}
                    rx={6}
                  />
                  <Path
                    d={`M ${x + barWidth / 2 - 6} ${tooltipY + tooltipHeight} L ${x + barWidth / 2} ${tooltipY + tooltipHeight + 6} L ${x + barWidth / 2 + 6} ${tooltipY + tooltipHeight}`}
                    fill={tooltipBg}
                    stroke={tooltipBorder}
                    strokeWidth={1}
                  />
                  <Rect
                    x={x + barWidth / 2 - 6}
                    y={tooltipY + tooltipHeight - 1}
                    width={12}
                    height={2}
                    fill={tooltipBg}
                  />
                  <SvgText
                    x={tooltipX + tooltipWidth / 2}
                    y={tooltipY + 18}
                    fill={textColor}
                    fontSize={11}
                    fontFamily={CHART_FONT_BOLD}
                    fontWeight="600"
                    textAnchor="middle"
                  >
                    {item[labelKey].substring(0, 16)}
                  </SvgText>
                  <SvgText
                    x={tooltipX + tooltipWidth / 2}
                    y={tooltipY + 36}
                    fill={CHART_COLORS[selectedBar % CHART_COLORS.length]}
                    fontSize={14}
                    fontFamily={CHART_FONT_BOLD}
                    fontWeight="700"
                    textAnchor="middle"
                  >
                    {formatCurrency(item[valueKey])}
                  </SvgText>
                </G>
              );
            })()}
          </Svg>

          {/* Touch/Hover overlay for bars */}
          {displayData.map((item, index) => {
            const { x, barHeight } = getBarPosition(index);
            return (
              <TouchableOpacity
                key={index}
                style={{
                  position: 'absolute',
                  left: x - 2,
                  top: 0,
                  width: barWidth + 4,
                  height: chartHeight + 10,
                  ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
                }}
                onPress={() => setSelectedBar(selectedBar === index ? null : index)}
                onPressIn={() => Platform.OS !== 'web' && setSelectedBar(index)}
                {...(Platform.OS === 'web' ? {
                  onMouseEnter: () => setSelectedBar(index),
                  onMouseLeave: () => setSelectedBar(null),
                } : {})}
                activeOpacity={1}
              />
            );
          })}
        </View>
      </ScrollView>

      {/* Legend */}
      <View style={styles.legend}>
        {displayData.slice(0, 5).map((item, index) => (
          <TouchableOpacity
            key={index}
            style={styles.legendItem}
            onPress={() => setSelectedBar(selectedBar === index ? null : index)}
          >
            <View style={[styles.legendColor, { backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }]} />
            <Text style={[styles.legendText, { color: textColor, fontWeight: selectedBar === index ? 'bold' : 'normal' }]} numberOfLines={1}>
              {item[labelKey].substring(0, 15)}: {formatCurrency(item[valueKey])}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
}

// Line Chart for Daily Sales with Tooltip
function LineChart({ data, title, colorScheme }) {
  const [selectedPoint, setSelectedPoint] = useState(null);
  const screenWidth = Dimensions.get('window').width - 32;
  const chartHeight = 180;
  const padding = { left: 50, right: 20, top: 20, bottom: 50 };
  const chartWidth = Math.max(screenWidth, data.length * 40);

  const textColor = colorScheme === 'dark' ? '#fff' : '#333';
  const gridColor = colorScheme === 'dark' ? '#444' : '#ddd';
  const tooltipBg = colorScheme === 'dark' ? '#333' : '#fff';
  const tooltipBorder = colorScheme === 'dark' ? '#555' : '#ccc';

  if (data.length === 0) {
    return (
      <View style={[styles.chartContainer, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#fff' }]}>
        <Text style={[styles.chartTitle, { color: textColor }]}>{title}</Text>
        <Text style={{ color: textColor, textAlign: 'center', padding: 20 }}>No data available</Text>
      </View>
    );
  }

  const maxValue = Math.max(...data.map(d => d.total), 1);
  const xStep = (chartWidth - padding.left - padding.right) / Math.max(data.length - 1, 1);

  const getY = (value) => chartHeight - padding.bottom - ((value / maxValue) * (chartHeight - padding.top - padding.bottom));
  const getX = (index) => padding.left + index * xStep;

  // Create path for line
  const linePath = data.map((d, i) => {
    const x = getX(i);
    const y = getY(d.total);
    return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
  }).join(' ');

  // Create area path
  const areaPath = linePath +
    ` L ${getX(data.length - 1)} ${chartHeight - padding.bottom}` +
    ` L ${padding.left} ${chartHeight - padding.bottom} Z`;

  return (
    <View style={[styles.chartContainer, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#fff' }]}>
      <Text style={[styles.chartTitle, { color: textColor }]}>{title}</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View style={{ width: chartWidth, height: chartHeight }}>
          <Svg width={chartWidth} height={chartHeight}>
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
              <G key={i}>
                <Line
                  x1={padding.left}
                  y1={getY(maxValue * ratio)}
                  x2={chartWidth - padding.right}
                  y2={getY(maxValue * ratio)}
                  stroke={gridColor}
                  strokeWidth={1}
                  strokeDasharray="4,4"
                />
                <SvgText x={5} y={getY(maxValue * ratio) + 4} fill={textColor} fontSize={9} fontFamily={CHART_FONT}>
                  {formatCurrency(maxValue * ratio)}
                </SvgText>
              </G>
            ))}

            {/* Area fill */}
            <Path d={areaPath} fill={CHART_COLORS[0]} fillOpacity={0.2} />

            {/* Line */}
            <Path d={linePath} fill="none" stroke={CHART_COLORS[0]} strokeWidth={2} />

            {/* Data points and labels */}
            {data.map((d, i) => {
              const x = getX(i);
              const y = getY(d.total);
              const isSelected = selectedPoint === i;

              return (
                <G key={i}>
                  {/* Visible data point */}
                  <Rect
                    x={x - (isSelected ? 6 : 4)}
                    y={y - (isSelected ? 6 : 4)}
                    width={isSelected ? 12 : 8}
                    height={isSelected ? 12 : 8}
                    fill={CHART_COLORS[0]}
                    rx={isSelected ? 6 : 4}
                  />
                  {data.length <= 15 && (
                    <SvgText
                      x={x}
                      y={chartHeight - 10}
                      fill={textColor}
                      fontSize={8}
                      fontFamily={CHART_FONT}
                      textAnchor="middle"
                      transform={`rotate(-45, ${x}, ${chartHeight - 10})`}
                    >
                      {d.date.substring(5)}
                    </SvgText>
                  )}
                </G>
              );
            })}

            {/* Vertical indicator line for selected point */}
            {selectedPoint !== null && data[selectedPoint] && (
              <Line
                x1={getX(selectedPoint)}
                y1={getY(data[selectedPoint].total)}
                x2={getX(selectedPoint)}
                y2={chartHeight - padding.bottom}
                stroke={CHART_COLORS[0]}
                strokeWidth={1}
                strokeDasharray="3,3"
              />
            )}

            {/* Tooltip */}
            {selectedPoint !== null && data[selectedPoint] && (() => {
              const d = data[selectedPoint];
              const x = getX(selectedPoint);
              const y = getY(d.total);
              const tooltipWidth = 140;
              const tooltipHeight = 70;
              let tooltipX = x - tooltipWidth / 2;
              let tooltipY = y - tooltipHeight - 12;
              if (tooltipX < 5) tooltipX = 5;
              if (tooltipX + tooltipWidth > chartWidth - 5) tooltipX = chartWidth - tooltipWidth - 5;
              if (tooltipY < 5) tooltipY = y + 15;

              return (
                <G>
                  <Rect
                    x={tooltipX}
                    y={tooltipY}
                    width={tooltipWidth}
                    height={tooltipHeight}
                    fill={tooltipBg}
                    stroke={tooltipBorder}
                    strokeWidth={1}
                    rx={6}
                  />
                  <SvgText
                    x={tooltipX + tooltipWidth / 2}
                    y={tooltipY + 16}
                    fill={textColor}
                    fontSize={10}
                    fontFamily={CHART_FONT}
                    textAnchor="middle"
                  >
                    {d.date}
                  </SvgText>
                  <SvgText
                    x={tooltipX + tooltipWidth / 2}
                    y={tooltipY + 34}
                    fill={CHART_COLORS[0]}
                    fontSize={14}
                    fontFamily={CHART_FONT_BOLD}
                    fontWeight="700"
                    textAnchor="middle"
                  >
                    Total: {formatCurrency(d.total)}
                  </SvgText>
                  <SvgText
                    x={tooltipX + tooltipWidth / 2}
                    y={tooltipY + 52}
                    fill={textColor}
                    fontSize={9}
                    fontFamily={CHART_FONT}
                    textAnchor="middle"
                  >
                    Tables: {formatCurrency(d.tables || 0)}
                  </SvgText>
                  <SvgText
                    x={tooltipX + tooltipWidth / 2}
                    y={tooltipY + 64}
                    fill={textColor}
                    fontSize={9}
                    fontFamily={CHART_FONT}
                    textAnchor="middle"
                  >
                    Deliveries: {formatCurrency(d.deliveries || 0)} | PC: {formatCurrency(d.computers || 0)}
                  </SvgText>
                </G>
              );
            })()}
          </Svg>

          {/* Touch/Hover overlays for data points */}
          {data.map((d, i) => {
            const x = getX(i);
            const y = getY(d.total);
            return (
              <TouchableOpacity
                key={i}
                style={{
                  position: 'absolute',
                  left: x - 15,
                  top: Math.max(0, y - 20),
                  width: 30,
                  height: 40,
                  borderRadius: 20,
                  ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
                }}
                onPress={() => setSelectedPoint(selectedPoint === i ? null : i)}
                {...(Platform.OS === 'web' ? {
                  onMouseEnter: () => setSelectedPoint(i),
                  onMouseLeave: () => setSelectedPoint(null),
                } : {})}
                activeOpacity={1}
              />
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
}

// Pie/Donut style summary
function SummaryCards({ data, colorScheme }) {
  const textColor = colorScheme === 'dark' ? '#fff' : '#333';

  return (
    <View style={styles.summaryContainer}>
      <View style={[styles.summaryCard, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#fff' }]}>
        <MaterialIcons name="attach-money" size={24} color={CHART_COLORS[0]} />
        <Text style={[styles.summaryValue, { color: textColor }]}>{formatCurrency(data.totalSales)}</Text>
        <Text style={[styles.summaryLabel, { color: textColor }]}>Total Sales</Text>
      </View>

      <View style={[styles.summaryCard, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#fff' }]}>
        <MaterialIcons name="computer" size={24} color={CHART_COLORS[1]} />
        <Text style={[styles.summaryValue, { color: textColor }]}>{formatCurrency(data.computerSales?.total || 0)}</Text>
        <Text style={[styles.summaryLabel, { color: textColor }]}>Computer Sales</Text>
      </View>

      <View style={[styles.summaryCard, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#fff' }]}>
        <MaterialIcons name="category" size={24} color={CHART_COLORS[2]} />
        <Text style={[styles.summaryValue, { color: textColor }]}>{data.categorySales?.length || 0}</Text>
        <Text style={[styles.summaryLabel, { color: textColor }]}>Categories</Text>
      </View>

      <View style={[styles.summaryCard, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#fff' }]}>
        <MaterialIcons name="inventory" size={24} color={CHART_COLORS[3]} />
        <Text style={[styles.summaryValue, { color: textColor }]}>{data.productSales?.length || 0}</Text>
        <Text style={[styles.summaryLabel, { color: textColor }]}>Products Sold</Text>
      </View>
    </View>
  );
}

// Comparison Line Chart for YoY and MoM data
function ComparisonLineChart({ series, title, colorScheme, xKey = 'dayOfYear', valueKey = 'cumulative', requireSeriesSelection = false }) {
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [hoveredPoint, setHoveredPoint] = useState(null); // { seriesIndex, pointIndex }
  const screenWidth = Dimensions.get('window').width - 32;
  const chartHeight = 200;
  const padding = { left: 60, right: 20, top: 20, bottom: 50 };

  const textColor = colorScheme === 'dark' ? '#fff' : '#333';
  const gridColor = colorScheme === 'dark' ? '#444' : '#ddd';
  const tooltipBg = colorScheme === 'dark' ? '#333' : '#fff';
  const tooltipBorder = colorScheme === 'dark' ? '#555' : '#ccc';

  // Filter out series with no data
  const validSeries = series.filter(s => s.data && s.data.length > 0);

  if (validSeries.length === 0) {
    return (
      <View style={[styles.chartContainer, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#fff' }]}>
        <Text style={[styles.chartTitle, { color: textColor }]}>{title}</Text>
        <Text style={{ color: textColor, textAlign: 'center', padding: 20 }}>No data available</Text>
      </View>
    );
  }

  // Find max values across all series
  const allValues = validSeries.flatMap(s => s.data.map(d => d[valueKey]));
  const allXValues = validSeries.flatMap(s => s.data.map(d => d[xKey]));
  const maxValue = Math.max(...allValues, 1);
  const maxX = Math.max(...allXValues, 1);
  const minX = Math.min(...allXValues, 0);

  const chartWidth = Math.max(screenWidth, (maxX - minX) * 3 + padding.left + padding.right);

  const getY = (value) => chartHeight - padding.bottom - ((value / maxValue) * (chartHeight - padding.top - padding.bottom));
  const getX = (xVal) => padding.left + ((xVal - minX) / (maxX - minX || 1)) * (chartWidth - padding.left - padding.right);

  // Create paths for each series
  const seriesPaths = validSeries.map((s, seriesIndex) => {
    const sortedData = [...s.data].sort((a, b) => a[xKey] - b[xKey]);
    const path = sortedData.map((d, i) => {
      const x = getX(d[xKey]);
      const y = getY(d[valueKey]);
      return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
    }).join(' ');
    return { ...s, path, sortedData, color: CHART_COLORS[seriesIndex % CHART_COLORS.length], seriesIndex };
  });

  // Format large numbers
  const formatValue = (val) => {
    if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M';
    if (val >= 1000) return (val / 1000).toFixed(1) + 'K';
    return val.toFixed(0);
  };

  return (
    <View style={[styles.chartContainer, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#fff' }]}>
      <Text style={[styles.chartTitle, { color: textColor }]}>{title}</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View style={{ width: chartWidth, height: chartHeight }}>
          <Svg width={chartWidth} height={chartHeight}>
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
              <G key={i}>
                <Line
                  x1={padding.left}
                  y1={getY(maxValue * ratio)}
                  x2={chartWidth - padding.right}
                  y2={getY(maxValue * ratio)}
                  stroke={gridColor}
                  strokeWidth={1}
                  strokeDasharray="4,4"
                />
                <SvgText x={5} y={getY(maxValue * ratio) + 4} fill={textColor} fontSize={9} fontFamily={CHART_FONT}>
                  {formatValue(maxValue * ratio)} €
                </SvgText>
              </G>
            ))}

            {/* Lines for each series */}
            {seriesPaths.map((s, idx) => (
              <Path
                key={idx}
                d={s.path}
                fill="none"
                stroke={s.color}
                strokeWidth={selectedSeries === null || selectedSeries === idx ? 2.5 : 1}
                opacity={selectedSeries === null || selectedSeries === idx ? 1 : 0.3}
              />
            ))}

            {/* Data points - show all if !requireSeriesSelection, or only selected series if requireSeriesSelection */}
            {seriesPaths.map((s, sIdx) => {
              const showPoints = !requireSeriesSelection || selectedSeries === sIdx;
              if (!showPoints) return null;
              return s.sortedData.map((d, pIdx) => {
                const x = getX(d[xKey]);
                const y = getY(d[valueKey]);
                const isHovered = hoveredPoint?.seriesIndex === sIdx && hoveredPoint?.pointIndex === pIdx;
                const isSeriesSelected = selectedSeries === null || selectedSeries === sIdx;
                return (
                  <G key={`${sIdx}-${pIdx}`}>
                    <Rect
                      x={x - (isHovered ? 6 : 4)}
                      y={y - (isHovered ? 6 : 4)}
                      width={isHovered ? 12 : 8}
                      height={isHovered ? 12 : 8}
                      fill={s.color}
                      rx={isHovered ? 6 : 4}
                      opacity={isSeriesSelected ? 1 : 0.3}
                    />
                  </G>
                );
              });
            })}

            {/* Tooltip */}
            {hoveredPoint !== null && seriesPaths[hoveredPoint.seriesIndex]?.sortedData[hoveredPoint.pointIndex] && (() => {
              const s = seriesPaths[hoveredPoint.seriesIndex];
              const d = s.sortedData[hoveredPoint.pointIndex];
              const x = getX(d[xKey]);
              const y = getY(d[valueKey]);
              const tooltipWidth = 140;
              const tooltipHeight = 65;
              let tooltipX = x - tooltipWidth / 2;
              let tooltipY = y - tooltipHeight - 12;
              if (tooltipX < 5) tooltipX = 5;
              if (tooltipX + tooltipWidth > chartWidth - 5) tooltipX = chartWidth - tooltipWidth - 5;
              if (tooltipY < 5) tooltipY = y + 15;

              // Format date display - show "Day X" for MoM or full date for YoY
              const dateDisplay = xKey === 'dayOfMonth' ? `Day ${d[xKey]}` : d.date;

              return (
                <G>
                  <Line x1={x} y1={y} x2={x} y2={chartHeight - padding.bottom} stroke={s.color} strokeWidth={1} strokeDasharray="3,3" />
                  <Rect x={tooltipX} y={tooltipY} width={tooltipWidth} height={tooltipHeight} fill={tooltipBg} stroke={tooltipBorder} strokeWidth={1} rx={6} />
                  <SvgText x={tooltipX + tooltipWidth / 2} y={tooltipY + 14} fill={s.color} fontSize={11} fontFamily={CHART_FONT_BOLD} fontWeight="600" textAnchor="middle">
                    {s.label}
                  </SvgText>
                  <SvgText x={tooltipX + tooltipWidth / 2} y={tooltipY + 30} fill={textColor} fontSize={10} fontFamily={CHART_FONT} textAnchor="middle">
                    {dateDisplay}
                  </SvgText>
                  <SvgText x={tooltipX + tooltipWidth / 2} y={tooltipY + 46} fill={s.color} fontSize={13} fontFamily={CHART_FONT_BOLD} fontWeight="700" textAnchor="middle">
                    {formatCurrency(d[valueKey])}
                  </SvgText>
                  <SvgText x={tooltipX + tooltipWidth / 2} y={tooltipY + 60} fill={textColor} fontSize={9} fontFamily={CHART_FONT} textAnchor="middle">
                    Daily: {formatCurrency(d.daily)}
                  </SvgText>
                </G>
              );
            })()}
          </Svg>

          {/* Touch/Hover overlays - show for all series if !requireSeriesSelection, or only selected series */}
          {seriesPaths.map((s, sIdx) => {
            const showOverlays = !requireSeriesSelection || selectedSeries === sIdx;
            if (!showOverlays) return null;
            return s.sortedData.map((d, pIdx) => {
              const x = getX(d[xKey]);
              const y = getY(d[valueKey]);
              return (
                <TouchableOpacity
                  key={`overlay-${sIdx}-${pIdx}`}
                  style={{
                    position: 'absolute',
                    left: x - 10,
                    top: Math.max(0, y - 10),
                    width: 20,
                    height: 20,
                    borderRadius: 10,
                    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
                  }}
                  onPress={() => setHoveredPoint(
                    hoveredPoint?.seriesIndex === sIdx && hoveredPoint?.pointIndex === pIdx
                      ? null
                      : { seriesIndex: sIdx, pointIndex: pIdx }
                  )}
                  {...(Platform.OS === 'web' ? {
                    onMouseEnter: () => setHoveredPoint({ seriesIndex: sIdx, pointIndex: pIdx }),
                    onMouseLeave: () => setHoveredPoint(null),
                  } : {})}
                  activeOpacity={1}
                />
              );
            });
          })}
        </View>
      </ScrollView>

      {/* Legend */}
      <View style={[styles.legend, { flexDirection: 'row', flexWrap: 'wrap' }]}>
        {seriesPaths.map((s, idx) => {
          const lastPoint = s.sortedData[s.sortedData.length - 1];
          const totalValue = lastPoint ? lastPoint[valueKey] : 0;
          return (
            <TouchableOpacity
              key={idx}
              style={[styles.legendItem, { marginRight: 16, opacity: selectedSeries === null || selectedSeries === idx ? 1 : 0.5 }]}
              onPress={() => {
                setSelectedSeries(selectedSeries === idx ? null : idx);
                setHoveredPoint(null);
              }}
            >
              <View style={[styles.legendColor, { backgroundColor: s.color }]} />
              <Text style={[styles.legendText, { color: textColor, fontWeight: selectedSeries === idx ? 'bold' : 'normal' }]}>
                {s.label}: {formatCurrency(totalValue)}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}

export default function SalesChartsScreen() {
  const colorScheme = useColorScheme();
  const [adminToken, setAdminToken] = useState('');
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Default to last 30 days
  const defaultEndDate = new Date();
  const defaultStartDate = new Date();
  defaultStartDate.setDate(defaultStartDate.getDate() - 30);

  const [range, setRange] = useState({
    startDate: defaultStartDate,
    endDate: defaultEndDate
  });

  useEffect(() => {
    async function fetchToken() {
      try {
        const storedAdminToken = await AsyncStorage.getItem('adminToken');
        if (storedAdminToken) setAdminToken(storedAdminToken);
      } catch (ex) {
        console.error('Error fetching admin token:', ex);
      }
    }
    fetchToken();
  }, []);

  const { loading, error, data, refetch } = useQuery(SALES_CHARTS_QUERY, {
    variables: {
      token: adminToken,
      dateFrom: range.startDate?.toISOString(),
      dateTo: range.endDate?.toISOString()
    },
    skip: !adminToken || !range.startDate || !range.endDate,
  });

  const onDismiss = () => setDatePickerOpen(false);

  const onConfirm = ({ startDate, endDate }) => {
    setDatePickerOpen(false);
    setRange({ startDate, endDate });
  };

  const textColor = colorScheme === 'dark' ? '#fff' : '#333';
  const bgColor = colorScheme === 'dark' ? '#000' : '#f2f2f7';

  if (!adminToken) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: bgColor }]}>
        <View style={styles.centered}>
          <Text style={{ color: textColor }}>Please log in as admin to view charts</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: bgColor }]}>
      {/* Header with date picker */}
      <View style={[styles.header, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#fff' }]}>
        <Text style={[styles.headerTitle, { color: textColor }]}>Sales Charts</Text>
        <TouchableOpacity
          style={[styles.dateButton, { backgroundColor: Config.companyColor }]}
          onPress={() => setDatePickerOpen(true)}
        >
          <MaterialIcons name="date-range" size={20} color="#fff" />
          <Text style={styles.dateButtonText}>
            {formatDate(range.startDate)} - {formatDate(range.endDate)}
          </Text>
        </TouchableOpacity>
      </View>

      <DatePickerModal
        locale="en"
        mode="range"
        visible={datePickerOpen}
        onDismiss={onDismiss}
        startDate={range.startDate}
        endDate={range.endDate}
        onConfirm={onConfirm}
      />

      {loading ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={Config.companyColor} />
          <Text style={{ color: textColor, marginTop: 10 }}>Loading charts...</Text>
        </View>
      ) : error ? (
        <View style={styles.centered}>
          <MaterialIcons name="error" size={48} color="#ff4444" />
          <Text style={{ color: textColor, marginTop: 10 }}>Error loading data</Text>
          <TouchableOpacity style={styles.retryButton} onPress={() => refetch()}>
            <Text style={{ color: '#fff' }}>Retry</Text>
          </TouchableOpacity>
        </View>
      ) : data?.salesCharts ? (
        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          {/* Summary Cards */}
          <SummaryCards data={data.salesCharts} colorScheme={colorScheme} />

          {/* Daily Sales Trend */}
          {data.salesCharts.dailySales?.length > 0 && (
            <LineChart
              data={data.salesCharts.dailySales}
              title="Daily Sales Trend"
              colorScheme={colorScheme}
            />
          )}

          {/* Sales by Category */}
          {data.salesCharts.categorySales?.length > 0 && (
            <BarChart
              data={data.salesCharts.categorySales}
              title="Sales by Category"
              colorScheme={colorScheme}
              maxBars={15}
            />
          )}

          {/* Top Products */}
          {data.salesCharts.productSales?.length > 0 && (
            <BarChart
              data={data.salesCharts.productSales}
              title="Top Products"
              colorScheme={colorScheme}
              maxBars={15}
            />
          )}

          {/* Computer Sales by Type */}
          {data.salesCharts.computerSales?.byType?.length > 0 && (
            <BarChart
              data={data.salesCharts.computerSales.byType}
              title="Computer Sales by Type"
              colorScheme={colorScheme}
              labelKey="type"
            />
          )}

          {/* Computer Sales by Unit */}
          {data.salesCharts.computerSales?.byComputer?.length > 0 && (
            <BarChart
              data={data.salesCharts.computerSales.byComputer}
              title="Sales by Computer"
              colorScheme={colorScheme}
              maxBars={20}
            />
          )}

          {/* Year over Year Comparison */}
          {data.salesCharts.yearOverYear?.length > 0 && (
            <ComparisonLineChart
              series={data.salesCharts.yearOverYear
                .filter(y => y.data && y.data.length > 0)
                .map(y => ({ label: y.year.toString(), data: y.data }))}
              title="Year over Year (Cumulative Sales YTD)"
              colorScheme={colorScheme}
              xKey="dayOfYear"
              requireSeriesSelection={true}
            />
          )}

          {/* Month over Month Comparison */}
          {data.salesCharts.monthOverMonth?.length > 0 && (
            <ComparisonLineChart
              series={data.salesCharts.monthOverMonth
                .filter(m => m.data && m.data.length > 0)
                .map(m => ({ label: m.label, data: m.data }))}
              title="Month over Month (Cumulative Sales)"
              colorScheme={colorScheme}
              xKey="dayOfMonth"
            />
          )}

          <View style={{ height: 40 }} />
        </ScrollView>
      ) : (
        <View style={styles.centered}>
          <Text style={{ color: textColor }}>No data available for selected period</Text>
        </View>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  dateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  dateButtonText: {
    color: '#fff',
    marginLeft: 8,
    fontSize: 12,
  },
  content: {
    flex: 1,
    padding: 16,
  },
  summaryContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  summaryCard: {
    width: '48%',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  summaryValue: {
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 8,
  },
  summaryLabel: {
    fontSize: 12,
    marginTop: 4,
    opacity: 0.7,
  },
  chartContainer: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  legend: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  legendColor: {
    width: 12,
    height: 12,
    borderRadius: 2,
    marginRight: 8,
  },
  legendText: {
    fontSize: 11,
    flex: 1,
  },
  retryButton: {
    marginTop: 16,
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: Config.companyColor,
    borderRadius: 8,
  },
});
